import React from "react";
import { CardContent, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { impactLevelInfo } from "../../app/riskLevel";
import { getDays, IMPERCEPTIBLE_WEDGE_THRESHOLD, MS_IN_HOUR, RouteMetricSummary } from "./RouteUtils";
import {
    DISTANCE_LABEL,
    DRIVING_TIME_LABEL,
    GOLDILOCKS,
    HOURS_UNIT,
    LEVEL_FIVE,
    LEVEL_FOUR,
    LEVEL_ONE,
    LEVEL_THREE,
    LEVEL_TWO,
    MILES_UNIT,
    NONE,
    OVERNIGHTS_LABEL,
    TOTAL_TIME_LABEL,
    WEATHER_DELAY_LABEL
} from "../UserVisibleStrings";
import { RouteMetrics } from "../../generated/gpb/api/route";
import DateChips from "./DateChips";
import MetricsChartPatterns from "./MetricsChartPatterns";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";

type RouteMetricsCardProps = {
    metrics: RouteMetricSummary,
    routeMetrics: Array<RouteMetrics>,
    selectedDates: Set<number>,
    onDateSelect: ( dateValue: Date, isShiftClick: boolean ) => void,
    setSelectedDates: React.Dispatch<React.SetStateAction<Set<number>>>,
};

export const valueFormatter = ( item: { value: number } ) => `${item.value.toFixed( 1 )}${HOURS_UNIT}`;

const RouteMetricsCard = ( { metrics, selectedDates, routeMetrics, onDateSelect, setSelectedDates }: RouteMetricsCardProps ) =>
{
    const days = getDays( routeMetrics );

    if ( days.length === 0 )
    {
        return null;
    }

    const data = [
        { name: GOLDILOCKS, value: metrics.travelDurationMsInRiskZoneGoldilocks / MS_IN_HOUR || 0, label: GOLDILOCKS, color: impactLevelInfo[GOLDILOCKS].color },
        { name: "No Impact", value: metrics.travelDistanceMiInRiskZoneBeyondForecast / MS_IN_HOUR || 0, label: "Beyond Forecast", color: "url(#NoDataPattern)" },
        { name: NONE, value: metrics.travelDurationMsInRiskZoneNone / MS_IN_HOUR || 0, label: NONE, color: "white" },
        { name: LEVEL_ONE, value: metrics.travelDurationMsInRiskZoneLow / MS_IN_HOUR || 0, label: LEVEL_ONE, color: impactLevelInfo[LEVEL_ONE].color },
        { name: LEVEL_TWO, value: metrics.travelDurationMsInRiskZoneLowMedium / MS_IN_HOUR || 0, label: LEVEL_TWO, color: impactLevelInfo[LEVEL_TWO].color },
        { name: LEVEL_THREE, value: metrics.travelDurationMsInRiskZoneMedium / MS_IN_HOUR || 0, label: LEVEL_THREE, color: impactLevelInfo[LEVEL_THREE].color },
        { name: LEVEL_FOUR, value: metrics.travelDurationMsInRiskZoneMediumHigh / MS_IN_HOUR || 0, label: LEVEL_FOUR, color: impactLevelInfo[LEVEL_FOUR].color },
        { name: LEVEL_FIVE, value: metrics.travelDurationMsInRiskZoneHigh / MS_IN_HOUR || 0, label: LEVEL_FIVE, color: impactLevelInfo[LEVEL_FIVE].color },
    ].filter( item => item.value > IMPERCEPTIBLE_WEDGE_THRESHOLD );

    const isMobile = useIsMobile();

    return (
        <CardContent sx = {{ padding: "5px", paddingBottom: "0px !important" }}>
            {days.length > 0 && (
                <div style = {{ paddingTop: "5px" }}>
                    <DateChips
                        dates = {days}
                        selectedDates = {selectedDates}
                        onDateSelect = {onDateSelect}
                        onClearSelection = {setSelectedDates}
                    />
                </div>
            )}
            <TableContainer sx = {{ paddingTop: "25px" }}>
                <Table size = "small">
                    {selectedDates.size === 0 ? (
                        <TableBody>
                            <TableRow>
                                <TableCell padding = "none">{DRIVING_TIME_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{( metrics.drivingDurationMs / MS_IN_HOUR ).toFixed( 1 )}{HOURS_UNIT}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell padding = "none">{TOTAL_TIME_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{( metrics.totalDurationMs / MS_IN_HOUR ).toFixed( 1 )}{HOURS_UNIT}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell padding = "none">{DISTANCE_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{metrics.distanceMi.toFixed( 0 )}{MILES_UNIT}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell padding = "none">{OVERNIGHTS_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{metrics.numberOfOvernights}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell padding = "none">{WEATHER_DELAY_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{( metrics.totalWeatherDelayDurationMs / MS_IN_HOUR ).toFixed( 1 )}{HOURS_UNIT}</TableCell>
                            </TableRow>
                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                <TableCell padding = "none">{DRIVING_TIME_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{( metrics.filteredDurationMs / MS_IN_HOUR ).toFixed( 0 )}{HOURS_UNIT}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell padding = "none">{DISTANCE_LABEL}</TableCell>
                                <TableCell align = "right" padding = "none">{metrics.filteredDistanceMi.toFixed( 0 )}{MILES_UNIT}</TableCell>
                            </TableRow>
                        </TableBody>
                    )}
                </Table>
            </TableContainer>

            <div style = {{ paddingTop: "5px" }}>
                <Typography variant = "h5" component = "div" sx = {{ fontSize: "18px" }}>
                    Impact Encountered
                </Typography>
                <PieChart
                    width = {360} //this parameter may need to be revisited when we check mobile compatability
                    height = {250}
                    margin = {{ right: 220 }}
                    sx = {{
                        "--my-custom-pattern": "url(#Pattern)",
                        "&&": { touchAction: "auto" },
                        "& .MuiPieArc-root": {
                            stroke: "#676767",
                            strokeWidth: 1,
                        },
                        "& .MuiChartsLegend-mark": {
                            stroke: "#676767",
                            strokeWidth: 1,
                        },
                    }}
                    series = {[ {
                        data,
                        highlightScope: { fade: "global" },
                        faded: { innerRadius: 0, additionalRadius: -15, color: "gray" },
                        valueFormatter,
                    } ]}
                    slotProps = {{
                        popper: {
                            sx: isMobile ? { marginLeft: "35px !important" } : undefined,
                            placement: isMobile ? "top-start" : "top-end",
                        },
                    }}
                    skipAnimation
                >
                    <MetricsChartPatterns/>
                </PieChart>
            </div>
        </CardContent>
    );
};

export default RouteMetricsCard;
